import React from "react";
import { Box, Typography, Card, CardContent, Divider } from "@mui/material";
import MetaStructuredTemplateDisplay from "./MetaStructuredTemplateDisplay";

// Iconsax — ClusterDen's standard icon library (see [[icon-library-iconsax]]), swapped in for
// the MUI icon-font set this file used before. No literal "reply-arrow" glyph exists in
// Iconsax, so QUICK_REPLY buttons reuse `ArrowLeft2` (same directional-arrow already used for
// "back" elsewhere in this app) as the closest available substitute.
import { Gallery, VideoSquare, DocumentText, ArrowLeft2, Call, ExportSquare, Copy } from "iconsax-react";

/** Shared width for the regular template card and the carousel's outer card (one carousel
 * card fills this width at a time, see the carousel branch below). Half the pane made it a
 * wide, short rectangle instead of a proportioned card (Rahim, 2026-08-25: "looks like a
 * rectangle and not like a square type") — a real pixel cap fixes the aspect ratio
 * regardless of pane size. */
const waRegularTemplateCardWidthSx = {
  maxWidth: {
    xs: "min(calc((var(--chatbox-width, 100vw)) * 0.5), calc(100vw - 72px))",
    sm: "min(300px, calc(var(--chatbox-width, 100vw) - 96px))",
  },
  width: { xs: "100%", sm: "auto" },
  boxSizing: "border-box",
};

/**
 * TemplateMessageDisplay
 * A component to display template messages in chat
 * Takes a stringified template body from a message and renders it
 * @param {{ templateBody: string|object, fromMe?: boolean, platform?: 'instagram'|'facebook', inboxStructuredTemplateSizing?: boolean }} props
 */
const TemplateMessageDisplay = ({ templateBody, fromMe = false, platform, inboxStructuredTemplateSizing = false }) => {
  // Parse the JSON string to get the template object
  let template = null;
  try {
    // Handle both string and object inputs
    template = typeof templateBody === 'string' ? JSON.parse(templateBody) : templateBody;
  } catch (error) {
    console.error("Error parsing template JSON:", error);
    return (
      <Typography variant="body2" color="error">
        Error displaying template message
      </Typography>
    );
  }

  if (!template) return null;

  // Instagram / Facebook Messenger structured template (Send API: template_type, no WhatsApp `components`)
  const isMetaStructuredTemplate = template.template_type && !template.components;
  if (isMetaStructuredTemplate) {
    return (
      <MetaStructuredTemplateDisplay
        template={template}
        fromMe={fromMe}
        platform={platform}
        inboxStructuredTemplateSizing={inboxStructuredTemplateSizing}
      />
    );
  }

  if (!template.components) return null;

  // Find each component by type (WhatsApp)
  const headerComp = template.components.find((comp) => comp.type === "HEADER" || comp.type === "header");
  const bodyComp = template.components.find((comp) => comp.type === "BODY" || comp.type === "body");
  const footerComp = template.components.find((comp) => comp.type === "FOOTER" || comp.type === "footer");
  const buttonsComp = template.components.find(
    (comp) => comp.type === "BUTTONS" || comp.type === "buttons"
  );
  const carouselComp = template.components.find(
    (comp) => comp.type === "CAROUSEL" || comp.type === "carousel"
  );

  // Determine header format (TEXT, IMAGE, VIDEO, DOCUMENT, etc.)
  const headerFormat = headerComp?.format;
  const headerMediaUrl =
    headerComp?.mediaUrl && String(headerComp.mediaUrl).trim()
      ? String(headerComp.mediaUrl).trim()
      : null;

  // Basic text for header, body, and footer
  const headerText = headerComp?.text;
  const bodyText = bodyComp?.text;
  const footerText = footerComp?.text;

  // Buttons array (each button has a type, e.g. QUICK_REPLY, PHONE, LINK, COPY_CODE, etc.)
  const buttonsData = buttonsComp?.buttons || [];
  
  // Carousel cards array
  const carouselCards = carouselComp?.cards || [];

  // If this is a carousel template, render carousel view
  if (carouselComp && carouselCards.length > 0) {
    // Real WhatsApp carousel: no header bar, one card fills the bubble width at a time,
    // horizontal scroll/swipe reveals the rest (Rahim, 2026-08-25: "make it just like
    // whatsapp does dont add top bar with weird text and icon... one image at a time
    // with horizontal scroll").
    return (
      <Card
        sx={{
          ...waRegularTemplateCardWidthSx,
          borderRadius: "14px",
          overflow: "hidden",
          // Fixed light colors, not theme-following — a template/attachment card reads as its
          // own white "document" regardless of app dark mode (Rahim, 2026-08-25: "do for all
          // the templates not just the meta one").
          border: "1px solid #DADCDE",
          backgroundColor: "#FFFFFF",
          padding: 0,
          boxShadow: "none",
        }}
      >
        {/* MUI bakes a `&:last-child { padding-bottom: 24px }` rule into CardContent that
          survives `padding: 0` — invisible when a footer/button row absorbs it, but very
          obvious when a button is the last element (Rahim, 2026-08-25: "too much space
          under the copy code text"). */}
      <CardContent sx={{ padding: 0, "&:last-child": { paddingBottom: 0 } }}>
          {/* Body text if exists */}
          {bodyComp && bodyComp.text && (
            <Typography
              variant="body2"
              component="div"
              sx={{
                padding: "10px 12px 6px 12px",
                fontSize: "0.9375rem",
                lineHeight: 1.4,
                color: "#050505",
                '& span[data-emoji]': {
                  fontSize: '1.5rem',
                  lineHeight: 1,
                  verticalAlign: 'middle',
                  display: 'inline-block',
                }
              }}
            >
              {String(bodyComp.text).split(/([\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]+)/u).map((part, idx) => {
                  const emojiRegex = /^[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]+$/u;
                  if (part && emojiRegex.test(part)) {
                    return <span key={idx} data-emoji>{part}</span>;
                  }
                  return part;
                })}
            </Typography>
          )}

          {/* Carousel cards: horizontal scroll strip, one card ≈ full bubble width so only
              one is visible at a time, snapping to the next on scroll/swipe. */}
          <Box
            sx={{
              display: "flex",
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              // scroll-snap-align only respects `scroll-padding`, not regular `padding` — without
              // this the browser auto-scrolled 12px on load to satisfy the snap alignment, which
              // visually cancelled out the left inset entirely (Rahim, 2026-08-25: "the inner
              // card is literally touching the border").
              scrollPaddingInline: "12px",
              gap: "8px",
              padding: "4px 12px 12px 12px",
              scrollbarWidth: "thin",
              // `scrollbar-color` is inherited, and the Inbox's dark-mode scroll pane sets it
              // globally (`slimScrollbarSx`, "#404040 #121212") — modern Chrome now honors
              // that standard property over the legacy `::-webkit-scrollbar-*` pseudo-elements
              // below, so without an explicit override here the inherited near-black value won
              // and painted a solid dark bar regardless of what the webkit rules said (Rahim,
              // 2026-08-25: "fix the scroll bar being black"). This card is a fixed-light
              // "document" always, so the scrollbar is pinned light too, not theme-conditional.
              scrollbarColor: "rgba(0,0,0,0.15) transparent",
              "&::-webkit-scrollbar": { height: 3 },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#00000026", borderRadius: 2 },
              "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
            }}
          >
            {carouselCards.map((card, cardIndex) => {
              const cardHeader = card.components?.find(c => c.type === "HEADER" || c.type === "header");
              const cardBody = card.components?.find(c => c.type === "BODY" || c.type === "body");
              const cardButtons = card.components?.find(c => c.type === "BUTTONS" || c.type === "buttons");
              const headerFormat = cardHeader?.format;

              return (
                <Box
                  key={cardIndex}
                  sx={{
                    flex: "0 0 100%",
                    scrollSnapAlign: "start",
                    border: "1px solid #DADCDE",
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  {/* Card header (image/video/document placeholder) — padded on the sides so
                      it reads as an image sitting inside the card, not a flush banner. */}
                  {cardHeader && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        aspectRatio: "4 / 3",
                        margin: "10px 10px 0 10px",
                        borderRadius: "8px",
                        backgroundColor: "#F0F2F5",
                      }}
                    >
                      {headerFormat === "VIDEO" ? (
                        <VideoSquare size={30} color="#65676B" variant="Linear" />
                      ) : headerFormat === "DOCUMENT" ? (
                        <DocumentText size={30} color="#65676B" variant="Linear" />
                      ) : (
                        <Gallery size={30} color="#65676B" variant="Linear" />
                      )}
                    </Box>
                  )}

                  {/* Card body */}
                  {cardBody && cardBody.text && (
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        padding: "8px 10px",
                        fontSize: "0.875rem",
                        color: "#050505",
                        '& span[data-emoji]': {
                          fontSize: '1.5rem',
                          lineHeight: 1,
                          verticalAlign: 'middle',
                          display: 'inline-block',
                        }
                      }}
                    >
                      {String(cardBody.text).split(/([\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]+)/u).map((part, idx) => {
                          const emojiRegex = /^[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]+$/u;
                          if (part && emojiRegex.test(part)) {
                            return <span key={idx} data-emoji>{part}</span>;
                          }
                          return part;
                        })}
                    </Typography>
                  )}

                  {/* Card buttons — same real-WhatsApp full-width divided-row style as the
                      regular template's buttons. */}
                  {cardButtons && cardButtons.buttons && cardButtons.buttons.length > 0 && (
                    <Box>
                      {cardButtons.buttons.map((btn, btnIndex) => (
                        <React.Fragment key={btnIndex}>
                          <Divider sx={{ borderColor: "#E6E7EB" }} />
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#1F7A5C",
                              fontSize: "0.875rem",
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "9px 10px",
                              "&:hover": { backgroundColor: "#1F7A5C14" },
                            }}
                          >
                            {btn.type === "QUICK_REPLY" && (
                              <Box sx={{ display: "flex", mr: 0.75 }}>
                                <ArrowLeft2 size={17} color="currentColor" variant="Linear" />
                              </Box>
                            )}
                            {btn.text || "Button"}
                          </Typography>
                        </React.Fragment>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Footer */}
          {footerComp && footerComp.text && (
            <Typography
              variant="caption"
              sx={{ display: "block", fontSize: "0.75rem", color: "#65676B", padding: "0 12px 10px 12px" }}
            >
              {footerComp.text}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }

  // Regular template (non-carousel)
  return (
    <Card
      sx={{
        ...waRegularTemplateCardWidthSx,
        borderRadius: "14px",
        overflow: "hidden",
        border: "1px solid #DADCDE",
        backgroundColor: "#FFFFFF",
        padding: 0,
        boxShadow: "none",
      }}
    >
      {/* MUI bakes a `&:last-child { padding-bottom: 24px }` rule into CardContent that
          survives `padding: 0` — invisible when a footer/button row absorbs it, but very
          obvious when a button is the last element (Rahim, 2026-08-25: "too much space
          under the copy code text"). */}
      <CardContent sx={{ padding: 0, "&:last-child": { paddingBottom: 0 } }}>
        {/* Header */}
        {headerComp && headerFormat === "TEXT" && (
          <Typography
            variant="subtitle1"
            component="div"
            fontWeight={600}
            sx={{
              fontSize: "0.9375rem",
              color: "#050505",
              padding: "12px 12px 2px 12px",
              '& span[data-emoji]': {
                fontSize: '1.5rem',
                lineHeight: 1,
                verticalAlign: 'middle',
                display: 'inline-block',
              }
            }}
          >
            {String(headerText).split(/([\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]+)/u).map((part, idx) => {
                const emojiRegex = /^[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]+$/u;
                if (part && emojiRegex.test(part)) {
                  return <span key={idx} data-emoji>{part}</span>;
                }
                return part;
              })}
          </Typography>
        )}

        {headerComp && headerFormat === "IMAGE" && (
          <Box sx={{ margin: "10px 10px 0 10px" }}>
            {headerMediaUrl ? (
              <Box
                component="img"
                src={headerMediaUrl}
                alt="Template header"
                sx={{ width: "100%", maxHeight: 280, display: "block", borderRadius: "8px", objectFit: "cover", color: "transparent" }}
              />
            ) : (
              // Clean placeholder — light inset box + small centered icon, not a bare icon
              // floating in a huge empty area (Rahim, 2026-08-25: "weird header img").
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  aspectRatio: "4 / 3",
                  borderRadius: "8px",
                  backgroundColor: "#F0F2F5",
                }}
              >
                <Gallery size={30} color="#65676B" variant="Linear" />
              </Box>
            )}
          </Box>
        )}

        {headerComp && headerFormat === "VIDEO" && (
          <Box sx={{ margin: "10px 10px 0 10px" }}>
            {headerMediaUrl ? (
              <Box
                component="video"
                src={headerMediaUrl}
                controls
                sx={{ width: "100%", maxHeight: 280, display: "block", borderRadius: "8px" }}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  aspectRatio: "4 / 3",
                  borderRadius: "8px",
                  backgroundColor: "#F0F2F5",
                }}
              >
                <VideoSquare size={30} color="#65676B" variant="Linear" />
              </Box>
            )}
          </Box>
        )}

        {headerComp && headerFormat === "DOCUMENT" && (
          <Box sx={{ margin: "10px 10px 0 10px" }}>
            {headerMediaUrl ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "16px",
                  borderRadius: "8px",
                  backgroundColor: "#F0F2F5",
                }}
              >
                <Typography
                  component="a"
                  href={headerMediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ color: "#1F7A5C", wordBreak: "break-all" }}
                >
                  Open document
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  aspectRatio: "4 / 3",
                  borderRadius: "8px",
                  backgroundColor: "#F0F2F5",
                }}
              >
                <DocumentText size={30} color="#65676B" variant="Linear" />
              </Box>
            )}
          </Box>
        )}

        {/* Body */}
        {bodyComp && (
          <Typography
            variant="body2"
            component="div"
            sx={{
              padding: "6px 12px",
              fontSize: '0.9375rem',
              lineHeight: 1.4,
              color: "#050505",
              whiteSpace: 'pre-wrap', // Preserve line breaks and whitespace
              '& span[data-emoji]': {
                fontSize: '1.5rem',
                lineHeight: 1,
                verticalAlign: 'middle',
                display: 'inline-block',
              }
            }}
          >
            {String(bodyText).split(/([\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]+)/u).map((part, idx) => {
                const emojiRegex = /^[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]+$/u;
                if (part && emojiRegex.test(part)) {
                  return <span key={idx} data-emoji>{part}</span>;
                }
                return part;
              })}
          </Typography>
        )}

        {/* Footer */}
        {footerComp && (
          <Typography
            variant="caption"
            sx={{ display: "block", fontSize: "0.75rem", color: "#65676B", padding: "2px 12px 8px 12px" }}
          >
            {footerText}
          </Typography>
        )}

        {/* Buttons — real WhatsApp template style: full-width rows separated by a thin
            divider between each one, no button chrome (no border/pill/background). */}
        {buttonsComp && buttonsData.length > 0 && (
          <Box>
            {buttonsData.map((btn, index) => {
              const buttonSx = {
                color: "#1F7A5C",
                fontSize: "0.875rem",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                boxSizing: "border-box",
                padding: "11px 12px",
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": { backgroundColor: "#1F7A5C14" },
              };
              const content = (() => {
                switch (btn.type?.toUpperCase()) {
                  case "QUICK_REPLY":
                    return (
                      <Typography variant="body2" sx={buttonSx}>
                        <Box sx={{ display: "flex", mr: 1 }}>
                          <ArrowLeft2 size={18} color="currentColor" variant="Linear" />
                        </Box>
                        {btn.text}
                      </Typography>
                    );

                  case "PHONE_NUMBER":
                    return (
                      <Typography variant="body2" sx={buttonSx}>
                        <Box sx={{ display: "flex", mr: 1 }}>
                          <Call size={18} color="currentColor" variant="Linear" />
                        </Box>
                        {btn.text}
                      </Typography>
                    );

                  case "URL":
                    return (
                      <Typography
                        variant="body2"
                        component="a"
                        href={btn.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={buttonSx}
                      >
                        <Box sx={{ display: "flex", mr: 1 }}>
                          <ExportSquare size={18} color="currentColor" variant="Linear" />
                        </Box>
                        {btn.text}
                      </Typography>
                    );

                  case "COPY_CODE":
                    return (
                      <Typography variant="body2" sx={buttonSx}>
                        <Box sx={{ display: "flex", mr: 1 }}>
                          <Copy size={18} color="currentColor" variant="Linear" />
                        </Box>
                        {btn.text || "Copy Code"}
                      </Typography>
                    );

                  default:
                    return <Typography sx={buttonSx}>{btn.text || "Button"}</Typography>;
                }
              })();
              return (
                <React.Fragment key={index}>
                  <Divider sx={{ borderColor: "#E6E7EB" }} />
                  {content}
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TemplateMessageDisplay;
